/**
 * Parses v-data-table options (page, itemsPerPage, sortBy, sortDesc) from URL query params.
 * Returns an options object merged over defaultOptions so callers always receive a full shape.
 *
 * @param {object} query - Vue Router route.query
 * @param {{ page, itemsPerPage, sortBy, sortDesc }} defaultOptions - fallback values
 * @returns {{ page, itemsPerPage, sortBy, sortDesc }}
 */
export function parseGridOptionsFromQuery (query, defaultOptions = {}) {
  const { pageNumber, pageSize, sortColumn, sortDirection } = query

  const parsedPageNumber = Number(pageNumber)
  const parsedPageSize = Number(pageSize)
  const hasValidPageNumber = Number.isInteger(parsedPageNumber) && parsedPageNumber > 0
  const hasValidPageSize = Number.isInteger(parsedPageSize) && (parsedPageSize > 0 || parsedPageSize === -1)
  const hasSortColumn = typeof sortColumn === 'string' && sortColumn.length > 0
  const isDesc = sortDirection === 'DESC'

  return {
    page: hasValidPageNumber ? parsedPageNumber : defaultOptions.page,
    itemsPerPage: hasValidPageSize ? parsedPageSize : defaultOptions.itemsPerPage,
    sortBy: hasSortColumn ? [sortColumn] : defaultOptions.sortBy,
    sortDesc: hasSortColumn ? [isDesc] : defaultOptions.sortDesc
  }
}

/**
 * Converts v-data-table options into the URL query param shape consumed by the server.
 * Returns an object with pageNumber and pageSize always set, and sortColumn / sortDirection
 * included only when a sort column is active (so callers can detect absence via undefined).
 *
 * @param {{ page, itemsPerPage, sortBy, sortDesc }} options
 * @returns {{ pageNumber: string, pageSize: string, sortColumn?: string, sortDirection?: string }}
 */
export function buildPaginationQueryParams ({ page, itemsPerPage, sortBy, sortDesc }) {
  const sortColumn = Array.isArray(sortBy) && sortBy.length ? sortBy[0] : null
  const sortDirection = Array.isArray(sortDesc) && sortDesc.length && sortDesc[0] ? 'DESC' : 'ASC'

  const params = {
    pageNumber: String(page),
    pageSize: String(itemsPerPage)
  }

  if (sortColumn) {
    params.sortColumn = sortColumn
    params.sortDirection = sortDirection
  }

  return params
}

/**
 * Returns true when the only thing that changed between two options snapshots is the sort
 * column or direction, AND the full un-paginated list is already loaded in the component.
 *
 * When this returns true the component can skip a backend fetch and let v-data-table sort
 * the already-loaded rows client-side.
 *
 * @param {object} newOptions
 * @param {object|undefined} previousOptions
 * @param {boolean} hasFetchedAll - whether the full un-paginated list is currently loaded
 * @returns {boolean}
 */
export function isAllRecordsSortOnlyChange (newOptions, previousOptions, hasFetchedAll) {
  if (!previousOptions || !hasFetchedAll) {
    return false
  }

  if (newOptions.itemsPerPage !== -1 || previousOptions.itemsPerPage !== -1) {
    return false
  }

  const currentSortBy = Array.isArray(newOptions.sortBy) && newOptions.sortBy.length ? newOptions.sortBy[0] : null
  const previousSortBy = Array.isArray(previousOptions.sortBy) && previousOptions.sortBy.length ? previousOptions.sortBy[0] : null

  const currentSortDesc = Array.isArray(newOptions.sortDesc) && newOptions.sortDesc.length ? newOptions.sortDesc[0] : false
  const previousSortDesc = Array.isArray(previousOptions.sortDesc) && previousOptions.sortDesc.length ? previousOptions.sortDesc[0] : false

  const sortChanged = currentSortBy !== previousSortBy || currentSortDesc !== previousSortDesc
  const pageOrSizeChanged = newOptions.page !== previousOptions.page || newOptions.itemsPerPage !== previousOptions.itemsPerPage

  return sortChanged && !pageOrSizeChanged
}

/**
 * Reads a positive integer id from a raw query value.
 *
 * Returns null for anything that is not one, which is what keeps a hand-edited, truncated or
 * repeated parameter (repeats arrive as an array) from reaching the API as NaN.
 *
 * Digits are matched before parsing rather than left to Number, which also accepts hex and
 * exponential notation: without this, ?clientId=0x9 would resolve to client 9 and ?clientId=1e2
 * to client 100.
 *
 * @param {*} value - a raw Vue Router query value
 * @returns {number|null}
 */
export function parseQueryId (value) {
  if (typeof value !== 'string' || !/^\d+$/.test(value.trim())) {
    return null
  }

  const parsed = Number(value)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

/**
 * Sets or removes a single key on a mutable query object.
 *
 * - When value is non-null and non-undefined the key is set to String(value).
 * - When value is null or undefined the key is removed so the URL stays clean.
 *
 * @param {object} query  - the mutable query object being built
 * @param {string} key    - URL query param name
 * @param {*}      value  - the value to store, or null/undefined to remove
 */
export function setOrDeleteQueryParam (query, key, value) {
  if (value !== null && value !== undefined) {
    query[key] = String(value)
  } else {
    delete query[key]
  }
}

/**
 * Returns true if any of the given keys differs between currentQuery and nextQuery.
 * Use this to avoid unnecessary router.replace calls when nothing actually changed.
 *
 * @param {object}   currentQuery - Vue Router route.query snapshot
 * @param {object}   nextQuery    - the newly built query object
 * @param {string[]} keys         - the query param names to compare
 * @returns {boolean}
 */
export function hasQueryChanged (currentQuery, nextQuery, keys) {
  return keys.some(key => currentQuery[key] !== nextQuery[key])
}
